import React, { useState, useEffect } from 'react';
import { 
  Bell, 
  BellOff, 
  BellRing, 
  Check, 
  X, 
  Loader2, 
  FileText, 
  Clock, 
  AlertTriangle,
  Ticket,
  Send,
  Building2,
  Settings
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import {
  isPushSupported,
  getPermissionStatus,
  subscribeToPush,
  unsubscribeFromPush,
  isSubscribed,
  getNotificationPreferences,
  saveNotificationPreferences,
  sendTestNotification
} from '../services/pushNotifications';

export const NotificationSettings = ({ isSuperAdmin = false }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingSend, setTestingSend] = useState(false);
  const [supported, setSupported] = useState(false);
  const [permission, setPermission] = useState('default');
  const [subscribed, setSubscribed] = useState(false);
  const [preferences, setPreferences] = useState({
    // Preferências para farmácias
    documentos_vencendo: true,
    verificacoes_pendentes: true,
    trial_expirando: true,
    ticket_respondido: true,
    // Preferências para Super Admin
    nova_farmacia: true,
    novo_ticket: true,
    // Email
    email_ativo: true
  });

  useEffect(() => {
    checkStatus();
  }, []);

  const checkStatus = async () => {
    setLoading(true);
    try {
      const pushSupported = isPushSupported();
      setSupported(pushSupported);
      setPermission(getPermissionStatus());
      
      // Só verifica inscrição se push for suportado
      if (pushSupported) {
        try {
          const subscriptionStatus = await isSubscribed();
          setSubscribed(subscriptionStatus);
        } catch (e) {
          console.log('Erro ao verificar inscrição:', e);
          setSubscribed(false);
        }
      }
      
      // Buscar preferências do servidor
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const prefs = await getNotificationPreferences(token);
          setPreferences(prev => ({ ...prev, ...prefs }));
        } catch (e) {
          console.log('Usando preferências padrão');
        }
      }
    } catch (error) {
      console.error('Erro ao verificar status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleSubscription = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      if (subscribed) {
        await unsubscribeFromPush(token);
        setSubscribed(false);
        toast.success('Notificações desativadas');
      } else {
        await subscribeToPush(token);
        setSubscribed(true);
        setPermission('granted');
        toast.success('Notificações ativadas com sucesso!');
      }
    } catch (error) {
      console.error('Erro:', error);
      if (error.message === 'Permissão negada') {
        toast.error('Você negou a permissão. Ative nas configurações do navegador.');
      } else {
        toast.error('Erro ao configurar notificações');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSavePreferences = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await saveNotificationPreferences(token, preferences);
      toast.success('Preferências salvas!');
    } catch (error) {
      toast.error('Erro ao salvar preferências');
    } finally {
      setSaving(false);
    }
  };

  const handleTestNotification = async () => {
    setTestingSend(true);
    try {
      const token = localStorage.getItem('token');
      await sendTestNotification(token);
      toast.success('Notificação de teste enviada!');
    } catch (error) {
      toast.error('Erro ao enviar notificação de teste');
    } finally {
      setTestingSend(false);
    }
  };

  const updatePreference = (key, value) => {
    setPreferences(prev => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-teal-600" />
        </CardContent>
      </Card>
    );
  }

  if (!supported) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BellOff className="w-5 h-5 text-slate-400" />
            Notificações Push
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200 p-4 rounded-lg">
            <p className="text-sm">
              Seu navegador não suporta notificações push. 
              Use Chrome, Firefox ou Edge para receber alertas em tempo real.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <BellRing className="w-5 h-5 text-teal-600" />
              Notificações Push
            </CardTitle>
            <CardDescription className="mt-1">
              Receba alertas importantes mesmo com o navegador fechado
            </CardDescription>
          </div>
          <Badge variant={subscribed ? "default" : "secondary"} className={subscribed ? "bg-green-100 text-green-800" : ""}>
            {subscribed ? "Ativo" : "Inativo"}
          </Badge>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* Status e Ativação */}
        <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
          <div className="flex items-center gap-3">
            {subscribed ? (
              <Bell className="w-5 h-5 text-green-600" />
            ) : (
              <BellOff className="w-5 h-5 text-slate-400" />
            )}
            <div>
              <p className="font-medium text-slate-900 dark:text-white">
                {subscribed ? 'Notificações Ativadas' : 'Notificações Desativadas'}
              </p>
              <p className="text-sm text-slate-500">
                {permission === 'denied' 
                  ? 'Permissão negada no navegador' 
                  : subscribed 
                    ? 'Você receberá alertas em tempo real'
                    : 'Clique para ativar'}
              </p>
            </div>
          </div>
          
          <Button
            onClick={handleToggleSubscription}
            disabled={saving || permission === 'denied'}
            variant={subscribed ? "outline" : "default"}
            className={!subscribed ? "bg-teal-600 hover:bg-teal-700" : ""}
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : subscribed ? (
              <BellOff className="w-4 h-4 mr-2" />
            ) : (
              <Bell className="w-4 h-4 mr-2" />
            )}
            {subscribed ? 'Desativar' : 'Ativar Notificações'}
          </Button>
        </div>

        {permission === 'denied' && (
          <div className="bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 p-4 rounded-lg text-sm">
            <p className="font-medium">Permissão bloqueada</p>
            <p>Para ativar, clique no ícone de cadeado na barra de endereço do navegador e permita notificações.</p>
          </div>
        )}

        {/* Preferências - só mostra se inscrito */}
        {subscribed && (
          <>
            <div className="border-t pt-6">
              <h3 className="font-medium text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                <Settings className="w-4 h-4" />
                Tipos de Notificação
              </h3>
              
              <div className="space-y-4">
                {/* Notificações para farmácias */}
                {!isSuperAdmin && (
                  <>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-amber-100 rounded-lg">
                          <FileText className="w-4 h-4 text-amber-600" />
                        </div>
                        <div>
                          <Label className="font-medium">Documentos Vencendo</Label>
                          <p className="text-sm text-slate-500">Alertas quando documentos estão próximos do vencimento</p>
                        </div>
                      </div>
                      <Switch 
                        checked={preferences.documentos_vencendo}
                        onCheckedChange={(v) => updatePreference('documentos_vencendo', v)}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-red-100 rounded-lg">
                          <Clock className="w-4 h-4 text-red-600" />
                        </div>
                        <div>
                          <Label className="font-medium">Verificações Pendentes</Label>
                          <p className="text-sm text-slate-500">Alertas de verificações não realizadas no horário</p>
                        </div>
                      </div>
                      <Switch 
                        checked={preferences.verificacoes_pendentes}
                        onCheckedChange={(v) => updatePreference('verificacoes_pendentes', v)}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-orange-100 rounded-lg">
                          <AlertTriangle className="w-4 h-4 text-orange-600" />
                        </div>
                        <div>
                          <Label className="font-medium">Trial Expirando</Label>
                          <p className="text-sm text-slate-500">Lembrete quando seu período de teste está acabando</p>
                        </div>
                      </div>
                      <Switch 
                        checked={preferences.trial_expirando}
                        onCheckedChange={(v) => updatePreference('trial_expirando', v)}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-green-100 rounded-lg">
                          <Ticket className="w-4 h-4 text-green-600" />
                        </div>
                        <div>
                          <Label className="font-medium">Respostas de Tickets</Label>
                          <p className="text-sm text-slate-500">Aviso quando o suporte responde seu ticket</p>
                        </div>
                      </div>
                      <Switch 
                        checked={preferences.ticket_respondido}
                        onCheckedChange={(v) => updatePreference('ticket_respondido', v)}
                      />
                    </div>
                  </>
                )}

                {/* Notificações para Super Admin */}
                {isSuperAdmin && (
                  <>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-purple-100 rounded-lg">
                          <Building2 className="w-4 h-4 text-purple-600" />
                        </div>
                        <div>
                          <Label className="font-medium">Nova Farmácia Cadastrada</Label>
                          <p className="text-sm text-slate-500">Alerta quando uma nova farmácia se cadastrar</p>
                        </div>
                      </div>
                      <Switch 
                        checked={preferences.nova_farmacia}
                        onCheckedChange={(v) => updatePreference('nova_farmacia', v)}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-100 rounded-lg">
                          <Ticket className="w-4 h-4 text-blue-600" />
                        </div>
                        <div>
                          <Label className="font-medium">Novo Ticket de Suporte</Label>
                          <p className="text-sm text-slate-500">Alerta quando um cliente abrir um ticket</p>
                        </div>
                      </div>
                      <Switch 
                        checked={preferences.novo_ticket}
                        onCheckedChange={(v) => updatePreference('novo_ticket', v)}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Botões de ação */}
            <div className="flex gap-3 pt-4 border-t">
              <Button 
                onClick={handleSavePreferences} 
                disabled={saving}
                className="bg-teal-600 hover:bg-teal-700"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Check className="w-4 h-4 mr-2" />
                )}
                Salvar Preferências
              </Button>
              
              <Button 
                variant="outline" 
                onClick={handleTestNotification}
                disabled={testingSend}
              >
                {testingSend ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Testar Notificação
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default NotificationSettings;
